import React, {
  Dispatch,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import { useVi, ViAction, ViActionType, ViState, ViThresholds } from "./ViContext";
import PartsterClient from "../services/PartsterClient";
import { ViError, ViErrorType } from "../models/Errors";
import { useHistory } from "react-router";
import { History } from "history";
import { AppContext, useApp } from "./AppContext";
import { createContext } from "react";
import { useContext } from "react";
import { useAuth } from "./AuthContext";
import { getPartsToReplace } from "../services";

interface PartsterState {
  status: PartsterActionType | "READY";
  isLoading: boolean;
}

interface PartsterContext extends PartsterState {
  dispatch: Dispatch<PartsterAction>;
}

export interface BundlePart {
  id: string;
  label: string;
  score: number;
  group: number;
}

enum PartsterActionType {
  GetBundleParts = "GET BUNDLE PARTS",
  GetBundlePartsRequested = "GET BUNDLE PARTS - REQUESTED",
  GetBundlePartsSuccess = "GET BUNDLE PARTS - SUCCESS",
  Error = "ERROR",
}

type PartsterAction =
  | {
      type: PartsterActionType.GetBundleParts;
      payload: { sgns: string[]; soleraModelId?: string };
    }
  | { type: PartsterActionType.GetBundlePartsRequested }
  | {
      type: PartsterActionType.GetBundlePartsSuccess;
      payload: { bundleParts: BundlePart[] };
    }
  | { type: PartsterActionType.Error; payload: { error: ViError } };

const initialState = {
  status: "READY" as const,
  isLoading: false,
};

const PartsterContext = createContext<PartsterContext | undefined>(undefined);
export const usePartster = () => {
  const context = useContext(PartsterContext);
  if (context === undefined)
    throw new ViError("usePartster must be used within a PartsterContext Provider");
  return context;
};

export const PartsterProvider = React.memo(({ children }: { children: ReactElement }) => {
  const App = useApp();
  const viState = useVi();
  const history = useHistory();
  const AuthContext = useAuth();
  const { actions: viActions, dispatch: viDispatch, thresholds } = useVi();
  const [actions, setActions] = useState<PartsterAction[]>([]);
  const [state, rawDispatch] = useReducer(reducer, initialState);
  const partsterClient = useMemo(() => PartsterClient.init(AuthContext), [AuthContext]);

  const dispatch = useCallback((action: PartsterAction) => {
    rawDispatch(action);
    setActions((actions) => [...actions, action]);
  }, []);

  useEffect(() => {
    for (const action of actions) {
      sideEffects(state, action, {
        dispatch,
        viDispatch,
        history,
        partsterClient,
        App,
      });
    }
    if (actions.length) setActions([]);
  }, [actions, history, dispatch, viDispatch, state, App, partsterClient]);

  useEffect(() => {
    for (const action of viActions) {
      rawDispatch(action);
      viSideEffects(state, action, {
        history,
        dispatch,
        viDispatch,
        thresholds,
        viState,
      });
    }
  }, [viActions, state, history, dispatch, viDispatch, thresholds, viState]);

  return (
    <PartsterContext.Provider value={{ ...state, dispatch }}>
      {children}
    </PartsterContext.Provider>
  );
});

const viSideEffects = async (
  state: PartsterState,
  action: ViAction,
  {
    history,
    dispatch,
    viDispatch,
    thresholds,
    viState,
  }: {
    history: History;
    dispatch: Dispatch<PartsterAction>;
    viDispatch: Dispatch<ViAction>;
    thresholds: ViThresholds;
    viState: ViState;
  }
) => {
  switch (action.type) {
    case ViActionType.ViResultsReceived:
      const { claimDamagedParts, carModel } = viState;
      const partsToReplace = getPartsToReplace(claimDamagedParts);
      const sgns = partsToReplace.map((part) => part.standardGuideNumber);
      const payload: { sgns: string[]; soleraModelId?: string } = { sgns };
      if (carModel?.model.axManufacturerCodeInt && carModel?.model.axModelCodeInt) {
        const { axManufacturerCodeInt, axModelCodeInt } = carModel?.model;
        payload["soleraModelId"] = `VDA ${axManufacturerCodeInt}${axModelCodeInt}`;
      }
      if (sgns.length)
        dispatch({
          type: PartsterActionType.GetBundleParts,
          payload,
        });
  }
};

const sideEffects = async (
  state: PartsterState,
  action: PartsterAction,
  {
    history,
    dispatch,
    viDispatch,
    partsterClient,
    App,
  }: {
    history: History;
    dispatch: Dispatch<PartsterAction>;
    viDispatch: Dispatch<ViAction>;
    partsterClient: typeof PartsterClient;
    App: AppContext;
  }
) => {
  try {
    switch (action.type) {
      case PartsterActionType.GetBundleParts:
        const { sgns, soleraModelId } = action.payload;
        const promise = (await partsterClient).getBundleParts(sgns, soleraModelId);
        dispatch({ type: PartsterActionType.GetBundlePartsRequested });
        const bundleParts: BundlePart[] = (await promise).out;
        dispatch({
          type: PartsterActionType.GetBundlePartsSuccess,
          payload: { bundleParts },
        });
        viDispatch({
          type: ViActionType.PartsterResultsReceived,
          payload: { bundleParts },
        });
        break;
    }
  } catch (e) {
    const error = new ViError(e);
    error.actionType = action.type;
    App.handleError && App.handleError(error);
    if (error.type !== ViErrorType.Warning && error.type !== ViErrorType.Silent) {
      dispatch({
        type: PartsterActionType.Error,
        payload: { error },
      });
    }
    // viDispatch({ type: ViActionType.Error, payload: { error } });
    return false;
  }
};

const reducer = (
  state: PartsterState,
  action: ViAction | PartsterAction
): PartsterState => {
  switch (action.type) {
    case PartsterActionType.GetBundleParts:
    case PartsterActionType.GetBundlePartsRequested: {
      return { ...state, status: action.type, isLoading: true };
    }

    case PartsterActionType.GetBundlePartsSuccess: {
      return { ...state, status: action.type, isLoading: false };
    }

    case PartsterActionType.Error: {
      return initialState;
    }

    default:
      return state;
  }
};
